import React from 'react';
import { FiTrendingUp, FiBarChart2, FiAlertTriangle, FiTarget, FiRefreshCw, FiShield, FiActivity, FiGrid, FiPieChart } from 'react-icons/fi';
import { Card, CardHeader, CardBody, PerformanceLineChart, EfficientFrontierChart, CorrelationHeatmap, HelpIcon, useLanguage } from '../components';
import { formatPct, formatNum } from './portfolioBuilderUtils';

const BenchmarkTab = ({ data, tt }) => {
  if (!data) return <p className="pb-analytics-empty">{tt('Click "Run" to compute benchmark-relative metrics.')}</p>;
  return (
    <div className="pb-analytics-result">
      <p className="pb-analytics-note">
        {tt('Benchmark')}: {data.benchmark || 'SPY'} | {tt('Period')}: {data.period || '1Y'}
        {data.uses_total_return ? ` | ${tt('Total-return adjusted')}` : ''}
      </p>
      <div className="pb-analytics-grid">
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Alpha (ann.)')} <HelpIcon text={tt('Excess return of the portfolio above the benchmark after adjusting for market risk (beta). Positive alpha indicates outperformance.')} /></span><span className="pb-stat-value">{formatPct(data.alpha * 100)}</span></div>
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Beta')} <HelpIcon text={tt('Sensitivity of portfolio returns to market (benchmark) movements. Beta of 1.0 means the portfolio moves in line with the market; above 1.0 means more volatile.')} /></span><span className="pb-stat-value">{formatNum(data.beta)}</span></div>
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Tracking Error')} <HelpIcon text={tt('Standard deviation of the difference between portfolio and benchmark returns. Lower values mean the portfolio closely follows the benchmark.')} /></span><span className="pb-stat-value">{formatPct(data.tracking_error * 100)}</span></div>
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Info Ratio')} <HelpIcon text={tt('Information Ratio: Alpha divided by Tracking Error. Measures risk-adjusted outperformance vs. benchmark. Values above 0.5 are considered good.')} /></span><span className="pb-stat-value">{formatNum(data.information_ratio)}</span></div>
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('R²')} <HelpIcon text={tt('R-squared: proportion of portfolio return variance explained by the benchmark (0 to 1). Higher R² means the benchmark is a good predictor of portfolio behavior.')} /></span><span className="pb-stat-value">{formatNum(data.r_squared)}</span></div>
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Observations')}</span><span className="pb-stat-value">{data.n_observations}</span></div>
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Portfolio Return')}</span><span className="pb-stat-value">{formatPct(data.ann_portfolio_return * 100)}</span></div>
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Benchmark Return')}</span><span className="pb-stat-value">{formatPct(data.ann_benchmark_return * 100)}</span></div>
      </div>
      {data.tickers_missing?.length > 0 && (
        <p className="pb-analytics-warn">{tt('Missing price data')}: {data.tickers_missing.join(', ')}</p>
      )}
    </div>
  );
};

const BacktestTab = ({ data, tt }) => {
  if (!data) return <p className="pb-analytics-empty">{tt('Click "Run" to simulate a historical backtest.')}</p>;
  const s = data.summary || {};
  return (
    <div className="pb-analytics-result">
      <div className="pb-analytics-grid">
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Total Return')}</span><span className="pb-stat-value">{formatPct(s.total_return_pct)}</span></div>
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Ann. Return')}</span><span className="pb-stat-value">{formatPct(s.annualised_return_pct)}</span></div>
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Ann. Volatility')}</span><span className="pb-stat-value">{formatPct(s.annualised_volatility_pct)}</span></div>
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Sharpe')} <HelpIcon text={tt('Risk-adjusted return: (Return − Risk-Free Rate) ÷ Volatility. Higher is better. Above 1.0 is good; above 2.0 is excellent.')} /></span><span className="pb-stat-value">{formatNum(s.sharpe_ratio)}</span></div>
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Max Drawdown')} <HelpIcon text={tt('Largest peak-to-trough decline during the backtest period. Represents the worst-case loss if you bought at the peak and sold at the trough.')} /></span><span className="pb-stat-value">{formatPct(s.max_drawdown_pct)}</span></div>
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Final Value')}</span><span className="pb-stat-value">${s.final_value?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '—'}</span></div>
        {s.total_transaction_cost > 0 && (
          <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Txn Cost')}</span><span className="pb-stat-value pb-negative">${s.total_transaction_cost?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
        )}
        {s.cost_bps > 0 && (
          <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Cost Rate')}</span><span className="pb-stat-value">{s.cost_bps} {tt('bps')}</span></div>
        )}
      </div>
      <p className="pb-analytics-note">{tt('Rebalance')}: {data.rebalance_frequency} | {data.n_rebalances} {tt('events')} | {tt('Period')}: {data.period}</p>
      {data.series?.length > 0 && (
        <div className="pb-backtest-chart">
          <PerformanceLineChart
            data={data.series.map(p => ({ label: p.date, value: p.value }))}
            height={220}
          />
        </div>
      )}
    </div>
  );
};

const StressTab = ({ data, tt }) => {
  if (!data) return <p className="pb-analytics-empty">{tt('Click "Run" to apply stress scenarios.')}</p>;
  return (
    <div className="pb-analytics-result">
      <p className="pb-analytics-note">{tt('Stress scenarios are estimated from your current portfolio weights using historical crisis shocks and factor-based assumptions. They are specific to this portfolio, not placeholder numbers.')}</p>
      <table className="pb-analytics-table">
        <thead><tr><th>{tt('Scenario')}</th><th>{tt('Portfolio Impact')}</th></tr></thead>
        <tbody>
          {(data.scenarios || []).map((sc) => (
            <tr key={sc.scenario}>
              <td>{sc.label}</td>
              <td className={sc.portfolio_impact_pct < 0 ? 'pb-negative' : 'pb-positive'}>
                {formatPct(sc.portfolio_impact_pct)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RiskDecompTab = ({ data, tt }) => {
  if (!data) return <p className="pb-analytics-empty">{tt('Click "Run" to decompose portfolio risk.')}</p>;
  return (
    <div className="pb-analytics-result">
      <div className="pb-analytics-grid" style={{ marginBottom: 12 }}>
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Portfolio Vol')}</span><span className="pb-stat-value">{formatPct(data.portfolio_volatility * 100)}</span></div>
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Assets Aligned')}</span><span className="pb-stat-value">{data.n_assets}</span></div>
      </div>
      <table className="pb-analytics-table">
        <thead><tr><th>{tt('Ticker')}</th><th>{tt('Weight')}</th><th>{tt('Marginal CTR')} <HelpIcon text={tt("Marginal Contribution to Risk: how much an incremental increase in this position's weight would change total portfolio volatility.")} /></th><th>{tt('% of Risk')}</th></tr></thead>
        <tbody>
          {(data.assets || []).slice(0, 15).map((a) => (
            <tr key={a.ticker}>
              <td>{a.ticker}</td>
              <td>{(a.weight * 100).toFixed(1)}%</td>
              <td>{formatNum(a.marginal_contribution, 6)}</td>
              <td className={a.pct_contribution < 0 ? 'pb-negative' : ''}>{formatPct(a.pct_contribution)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DriftTab = ({ data, tt }) => {
  if (!data) return <p className="pb-analytics-empty">{tt('Click "Run" to check portfolio drift.')}</p>;
  return (
    <div className="pb-analytics-result">
      <div className="pb-analytics-grid" style={{ marginBottom: 12 }}>
        <div className="pb-analytics-stat">
          <span className="pb-stat-label">{tt('Drift Score')} <HelpIcon text={tt('Measures how far current portfolio weights have drifted from target allocations due to market movements. Higher scores indicate a greater need to rebalance.')} /></span>
          <span className="pb-stat-value">{formatNum(data.drift_score, 2)}%</span>
        </div>
        <div className="pb-analytics-stat">
          <span className="pb-stat-label">{tt('Needs Rebalance')}</span>
          <span className={`pb-stat-value ${data.needs_rebalance ? 'pb-negative' : 'pb-positive'}`}>
            {data.needs_rebalance ? tt('YES') : tt('No')}
          </span>
        </div>
        <div className="pb-analytics-stat">
          <span className="pb-stat-label">{tt('Threshold')}</span>
          <span className="pb-stat-value">{data.rebalance_threshold_pct}%</span>
        </div>
      </div>
      {data.recommendations?.length > 0 && (
        <>
          <h4 className="pb-sub-title" style={{ margin: '10px 0 6px', fontSize: 13 }}>{tt('Rebalance Actions')}</h4>
          <table className="pb-analytics-table">
            <thead><tr><th>{tt('Ticker')}</th><th>{tt('Action')}</th><th>{tt('Drift')}</th><th>{tt('Target')}</th><th>{tt('Current')}</th></tr></thead>
            <tbody>
              {data.recommendations.map((r) => (
                <tr key={r.ticker}>
                  <td>{r.ticker}</td>
                  <td className={r.action === 'BUY' ? 'pb-positive' : 'pb-negative'}>{r.action}</td>
                  <td>{formatPct(r.drift_pct)}</td>
                  <td>{formatPct(r.target_weight_pct)}</td>
                  <td>{formatPct(r.current_weight_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

const BrinsonTab = ({ data, tt }) => {
  if (!data) return <p className="pb-analytics-empty">{tt('Click "Run" to decompose performance into allocation vs. selection effects.')}</p>;
  return (
    <div className="pb-analytics-result">
      <p className="pb-analytics-note">
        {tt('Method')}: {data.attribution_method === 'sector_proxy' ? tt('Sector proxy decomposition') : tt('Single-index fallback')}
      </p>
      <div className="pb-analytics-grid" style={{ marginBottom: 12 }}>
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Portfolio Return')}</span><span className="pb-stat-value">{formatPct(data.portfolio_return_pct)}</span></div>
        <div className="pb-analytics-stat"><span className="pb-stat-label">{tt('Benchmark Return')}</span><span className="pb-stat-value">{formatPct(data.benchmark_return_pct)}</span></div>
        <div className="pb-analytics-stat">
          <span className="pb-stat-label">{tt('Active Return')} <HelpIcon text={tt('Difference between portfolio and benchmark returns. Positive = outperformance.')} /></span>
          <span className={`pb-stat-value ${data.active_return_pct < 0 ? 'pb-negative' : 'pb-positive'}`}>{formatPct(data.active_return_pct)}</span>
        </div>
        <div className="pb-analytics-stat">
          <span className="pb-stat-label">{tt('Allocation Effect')} <HelpIcon text={tt('Return from overweighting/underweighting sectors that outperformed/underperformed the benchmark. Positive means good sector bets.')} /></span>
          <span className={`pb-stat-value ${data.total_allocation_pct < 0 ? 'pb-negative' : 'pb-positive'}`}>{formatPct(data.total_allocation_pct)}</span>
        </div>
        <div className="pb-analytics-stat">
          <span className="pb-stat-label">{tt('Selection Effect')} <HelpIcon text={tt('Return from picking better-performing stocks within each sector vs. benchmark. Positive means good stock picks.')} /></span>
          <span className={`pb-stat-value ${data.total_selection_pct < 0 ? 'pb-negative' : 'pb-positive'}`}>{formatPct(data.total_selection_pct)}</span>
        </div>
        <div className="pb-analytics-stat">
          <span className="pb-stat-label">{tt('Interaction')} <HelpIcon text={tt('Combined effect of both overweighting sectors and picking good stocks within them. Often small.')} /></span>
          <span className={`pb-stat-value ${data.total_interaction_pct < 0 ? 'pb-negative' : 'pb-positive'}`}>{formatPct(data.total_interaction_pct)}</span>
        </div>
      </div>
      {data.sectors?.length > 0 && (
        <>
          <h4 className="pb-sub-title" style={{ margin: '10px 0 6px', fontSize: 13 }}>{tt('By Sector')} ({data.period})</h4>
          <table className="pb-analytics-table">
            <thead><tr><th>{tt('Sector')}</th><th>{tt('Port Wt')}</th><th>{tt('Bench Wt')}</th><th>{tt('Port Ret')}</th><th>{tt('Alloc')}</th><th>{tt('Select')}</th></tr></thead>
            <tbody>
              {data.sectors.map((s) => (
                <tr key={s.sector}>
                  <td>{s.sector}</td>
                  <td>{formatPct(s.port_weight_pct)}</td>
                  <td>{formatPct(s.bench_weight_pct)}</td>
                  <td className={s.port_return_pct < 0 ? 'pb-negative' : ''}>{formatPct(s.port_return_pct)}</td>
                  <td className={s.allocation_pct < 0 ? 'pb-negative' : 'pb-positive'}>{formatPct(s.allocation_pct)}</td>
                  <td className={s.selection_pct < 0 ? 'pb-negative' : 'pb-positive'}>{formatPct(s.selection_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

const AnalyticsPanel = ({
  activeTab,
  onTabChange,
  benchmarkData,
  backtestData,
  stressData,
  riskDecompData,
  driftData,
  brinsonData,
  frontierData,
  correlation,
  isBackendFrontierActive,
  frontierFallbackReason,
  onRun,
  loading,
  costBps,
  onCostBpsChange,
  analyticsPeriod,
  onAnalyticsPeriodChange,
  analyticsBenchmarkTicker,
  onAnalyticsBenchmarkTickerChange,
  analyticsRebalanceFrequency,
  onAnalyticsRebalanceFrequencyChange,
  driftThresholdPct,
  onDriftThresholdPctChange,
}) => {
  const { tt } = useLanguage();
  const periodOptions = [
    { value: '1M', label: tt('1 month') },
    { value: '3M', label: tt('3 months') },
    { value: '6M', label: tt('6 months') },
    { value: '1Y', label: tt('1 year') },
    { value: '3Y', label: tt('3 years') },
    { value: '5Y', label: tt('5 years') },
  ];
  const analyticsTabs = [
    { key: 'frontier', label: tt('Frontier'), icon: FiActivity },
    { key: 'correlation', label: tt('Correlation'), icon: FiGrid },
    { key: 'benchmark', label: tt('Benchmark'), icon: FiTrendingUp },
    { key: 'backtest', label: tt('Backtest'), icon: FiBarChart2 },
    { key: 'stress', label: tt('Stress Test'), icon: FiAlertTriangle },
    { key: 'riskDecomp', label: tt('Risk Decomp'), icon: FiTarget },
    { key: 'drift', label: tt('Drift'), icon: FiRefreshCw },
    { key: 'brinson', label: tt('Attribution'), icon: FiPieChart },
  ];
  const needsRun = !['frontier', 'correlation'].includes(activeTab);
  return (
  <Card variant="default" className="pb-analytics-card">
    <CardHeader>
      <div className="pb-chart-title-row">
        <FiShield size={18} className="pb-icon-cyan" />
        <h3 className="pb-sub-title">{tt('Advanced Analytics')}</h3>
        <span className="pb-model-badge backend">{tt('Phase 3')}</span>
      </div>
    </CardHeader>
    <CardBody>
      <div className="pb-analytics-tabs" role="tablist" aria-label={tt('Advanced analytics tabs')}>
        {analyticsTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              className={`pb-analytics-tab ${isActive ? 'active' : ''}`}
              onClick={() => onTabChange(tab.key)}
              role="tab"
              aria-selected={isActive}
              aria-controls={`analytics-panel-${tab.key}`}
              id={`analytics-tab-${tab.key}`}
            >
              <Icon size={14} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
      <p className="pb-analytics-note">{tt('Each analytics tab below is calculated from your saved holdings, weights, historical price data, and factor exposures in the database.')}</p>
      <div className="pb-analytics-actions">
        {(activeTab === 'benchmark' || activeTab === 'brinson') && (
          <>
            <label className="pb-cost-input-label">
              {tt('Benchmark')}
              <input
                type="text"
                value={analyticsBenchmarkTicker}
                onChange={(e) => onAnalyticsBenchmarkTickerChange(String(e.target.value || '').toUpperCase().replace(/[^A-Z0-9.-]/g, '').slice(0, 10))}
                className="pb-cost-input"
                placeholder={tt('SPY')}
              />
            </label>
            <label className="pb-cost-input-label">
              {tt('Period')}
              <select
                value={analyticsPeriod}
                onChange={(e) => onAnalyticsPeriodChange(e.target.value)}
                className="pb-cost-input pb-cost-select"
              >
                {periodOptions.map((period) => (
                  <option key={period.value} value={period.value}>{period.label}</option>
                ))}
              </select>
            </label>
          </>
        )}
        {activeTab === 'backtest' && (
          <>
          <label className="pb-cost-input-label">
            {tt('Period')}
            <select
              value={analyticsPeriod}
              onChange={(e) => onAnalyticsPeriodChange(e.target.value)}
              className="pb-cost-input pb-cost-select"
            >
              {periodOptions.map((period) => (
                <option key={period.value} value={period.value}>{period.label}</option>
              ))}
            </select>
          </label>
          <label className="pb-cost-input-label">
            {tt('Rebalance')}
            <select
              value={analyticsRebalanceFrequency}
              onChange={(e) => onAnalyticsRebalanceFrequencyChange(e.target.value)}
              className="pb-cost-input pb-cost-select"
            >
              <option value="daily">{tt('daily')}</option>
              <option value="weekly">{tt('weekly')}</option>
              <option value="monthly">{tt('monthly')}</option>
              <option value="quarterly">{tt('quarterly')}</option>
              <option value="none">{tt('none')}</option>
            </select>
          </label>
          <label className="pb-cost-input-label">
            {tt('Txn Cost')} <HelpIcon text={tt('Transaction cost in basis points (1 bps = 0.01%). Applied to each rebalance trade. Typical retail: 5–15 bps; institutional: 1–5 bps.')} />
            <input
              type="number"
              min="0" max="100" step="1"
              value={costBps}
              onChange={(e) => onCostBpsChange(Number(e.target.value) || 0)}
              className="pb-cost-input"
            />
            {tt('bps')}
          </label>
          </>
        )}
        {activeTab === 'drift' && (
          <label className="pb-cost-input-label">
            {tt('Threshold (%)')}
            <input
              type="number"
              min="1"
              max="20"
              step="0.5"
              value={driftThresholdPct}
              onChange={(e) => onDriftThresholdPctChange(Number(e.target.value) || 5)}
              className="pb-cost-input"
            />
          </label>
        )}
        {needsRun && (
          <button
            className="pb-analytics-run-btn"
            onClick={() => onRun(activeTab)}
            disabled={loading}
          >
            {loading ? tt('Running...') : tt('Run')}
          </button>
        )}
      </div>
      <div className="pb-analytics-body">
        {activeTab === 'frontier' && (
          <div id="analytics-panel-frontier" role="tabpanel" aria-labelledby="analytics-tab-frontier">
          {frontierData && frontierData.length > 1
            ? <EfficientFrontierChart data={frontierData} height={350} />
            : <p className="pb-analytics-empty">
                {frontierFallbackReason
                  ? tt('Efficient frontier data is temporarily unavailable. Please retry after the backend analytics refreshes.')
                  : tt('Add at least 2 holdings to view the efficient frontier.')}
              </p>
          }
          </div>
        )}
        {activeTab === 'correlation' && (
          <div id="analytics-panel-correlation" role="tabpanel" aria-labelledby="analytics-tab-correlation">
          {correlation && correlation.matrix && correlation.matrix.length > 0
            ? (
              <div className="pb-heatmap-scroll-wrap">
                <CorrelationHeatmap labels={correlation.labels} matrix={correlation.matrix} />
              </div>
            )
            : <p className="pb-analytics-empty">{tt('Add holdings to view the correlation heatmap.')}</p>
          }
          </div>
        )}
        {activeTab === 'benchmark' && <div id="analytics-panel-benchmark" role="tabpanel" aria-labelledby="analytics-tab-benchmark"><BenchmarkTab data={benchmarkData} tt={tt} /></div>}
        {activeTab === 'backtest' && <div id="analytics-panel-backtest" role="tabpanel" aria-labelledby="analytics-tab-backtest"><BacktestTab data={backtestData} tt={tt} /></div>}
        {activeTab === 'stress' && <div id="analytics-panel-stress" role="tabpanel" aria-labelledby="analytics-tab-stress"><StressTab data={stressData} tt={tt} /></div>}
        {activeTab === 'riskDecomp' && <div id="analytics-panel-riskDecomp" role="tabpanel" aria-labelledby="analytics-tab-riskDecomp"><RiskDecompTab data={riskDecompData} tt={tt} /></div>}
        {activeTab === 'drift' && <div id="analytics-panel-drift" role="tabpanel" aria-labelledby="analytics-tab-drift"><DriftTab data={driftData} tt={tt} /></div>}
        {activeTab === 'brinson' && <div id="analytics-panel-brinson" role="tabpanel" aria-labelledby="analytics-tab-brinson"><BrinsonTab data={brinsonData} tt={tt} /></div>}
      </div>
    </CardBody>
  </Card>
  );
};

export default AnalyticsPanel;
